// useViewTransition: wraps every react-router pathname change in a
// document.startViewTransition() when the browser supports it. When the
// API is missing the effect is a no-op and the route swap is instant, so
// this hook is safe to mount at the router root regardless of platform.
//
// Non-destructive: no library, no build change. Requires only a companion
// CSS block in /src/index.css (::view-transition-old(root), etc.) which
// controls the fade timing.
//
// Reference: https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

export default function useViewTransition() {
  const location = useLocation();
  const lastPathRef = useRef(location.pathname);

  useEffect(() => {
    // Same path (e.g. only a search-param or hash change). Skip.
    if (lastPathRef.current === location.pathname) return;
    lastPathRef.current = location.pathname;

    // Feature detect. Chromium 111+, Safari 18+, Firefox 141+.
    if (typeof document === "undefined" || !document.startViewTransition) {
      return;
    }
    // Reduced-motion users opt out of the fade.
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    // startViewTransition takes a callback. React has already committed
    // the DOM by the time this effect fires, so calling with an empty
    // callback still snapshots old-and-new and cross-fades between them.
    try {
      document.startViewTransition(() => {});
    } catch { /* silent: not fatal */ }
  }, [location.pathname]);
}
